import { quat } from 'gl-matrix';

/**
 * Quaternion utilities for composing object placements across asset / mesh /
 * scene layers. The engine historically stored rotations as Euler XYZ degrees
 * and composed them by component-wise addition, which is mathematically wrong
 * for any non-trivial multi-axis rotation. These helpers replace that path.
 *
 * **Layering rules (canonical for the engine)**:
 *
 * - **Position** — each layer that supplies a position **overwrites** the
 *   previous layer's position. Most-specific wins (object > mesh > asset).
 *   Position lives in world axes; it is NOT rotated into the parent's frame.
 * - **Scale** — each layer **multiplies** the running scale (uniform float).
 * - **Rotation** — each layer carries a quaternion `[x, y, z, w]`; layers
 *   compose left-to-right via {@link composeWorldRotation}. Component-wise
 *   Euler addition is mathematically wrong for any non-trivial multi-axis
 *   case and is forbidden in this codebase.
 *
 * Attachment-offset composition is intentionally separate (see
 * `gltf/JointAttachment.ts`) because it operates in local space and rotates
 * translation into the offset's frame (`tLocal = r·t`). Do not unify the two.
 *
 * **Convention**: Euler rotations apply as `R = Rx(α) · Ry(β) · Rz(γ)` —
 * i.e. rotate around X first, then Y, then Z (post-multiplied onto a running
 * matrix). This matches `TRS.changeImpl` (which calls
 * `mat4.rotateX → rotateY → rotateZ` on the same matrix) so a TRS built from
 * a quat produced here renders identically to a TRS built from the same
 * Euler triplet.
 *
 * **Why not `quat.fromEuler`**: gl-matrix's `quat.fromEuler` defaults to
 * `"zyx"` order, which does NOT match the TRS chain. Calling it directly is
 * a known footgun in this codebase. Always go through this helper.
 */

/**
 * Build a quaternion from Euler-XYZ degrees using the engine's
 * `Rx · Ry · Rz` convention (post-multiplied, matching `TRS.changeImpl`).
 */
export function eulerDegToQuat(x: number, y: number, z: number, out?: quat): quat {
  const result = out ?? quat.create();
  const deg = Math.PI / 180;
  // qX, qY, qZ each represent a single-axis rotation; compose left-to-right.
  const qx = quat.setAxisAngle(quat.create(), [1, 0, 0], x * deg);
  const qy = quat.setAxisAngle(quat.create(), [0, 1, 0], y * deg);
  const qz = quat.setAxisAngle(quat.create(), [0, 0, 1], z * deg);
  // R = Rx * Ry * Rz means quat is qx ∘ qy ∘ qz under left-to-right vector
  // transform convention used by gl-matrix (`quat.mul(out, a, b)` produces
  // a quaternion whose application equals first applying a then b — which is
  // the post-multiply convention, matching the TRS chain).
  quat.multiply(result, qx, qy);
  quat.multiply(result, result, qz);
  return result;
}

/**
 * Compose a list of rotation layers into a single quaternion. Layers apply
 * outermost-first (left-to-right), the same convention used in
 * `eulerDegToQuat`. An empty array returns the identity quaternion.
 *
 * Use this anywhere the engine previously summed Euler triplets across
 * asset / mesh / scene layers — the resulting quat is the mathematically
 * correct composition.
 */
export function composeWorldRotation(layers: readonly quat[]): quat {
  const out = quat.create();
  for (const layer of layers) {
    quat.multiply(out, out, layer);
  }
  return out;
}

/**
 * Decompose a quaternion into Euler XYZ degrees matching the engine's
 * `Rx · Ry · Rz` post-multiplied convention (the inverse of
 * {@link eulerDegToQuat}). Used at UI input boundaries (TRS editor fields)
 * — the canonical persisted form remains the quat. Has a documented
 * gimbal-lock branch at ±90° pitch (β) where γ collapses to 0.
 */
export function quatToEulerDeg(q: quat): [number, number, number] {
  // R = Rx(α) · Ry(β) · Rz(γ). Extracted from the rotation matrix:
  //   R[0][2] = sin(β)
  //   R[1][2] = -sin(α)cos(β)
  //   R[2][2] = cos(α)cos(β)
  //   R[0][1] = -sin(γ)cos(β)
  //   R[0][0] = cos(γ)cos(β)
  const x = q[0], y = q[1], z = q[2], w = q[3];
  const RAD_TO_DEG = 180 / Math.PI;
  const sinB = 2 * (x * z + w * y);
  if (Math.abs(sinB) >= 0.99999) {
    const beta = Math.sign(sinB) * Math.PI / 2;
    // Gimbal lock: collapse γ to 0
    const alpha = Math.atan2(2 * (y * z + w * x), 1 - 2 * (x * x + y * y));
    return [alpha * RAD_TO_DEG, beta * RAD_TO_DEG, 0];
  }
  const beta = Math.asin(sinB);
  const alpha = Math.atan2(-2 * (y * z - w * x), 1 - 2 * (x * x + y * y));
  const gamma = Math.atan2(-2 * (x * y - w * z), 1 - 2 * (y * y + z * z));
  return [alpha * RAD_TO_DEG, beta * RAD_TO_DEG, gamma * RAD_TO_DEG];
}
